import { CustomFieldFormats, CustomFieldTypes } from '@generated/graphql';
import type { CustomField, CustomOption } from '@interfaces/customField';
import type { ForeignTaxInformation } from '@interfaces/foreignTaxInformation';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { truncateNumber } from '@utils/common';
import { evaluateTriggerRules } from '@utils/evaluateTriggerRules';
import { t } from 'i18next';
import {
  isArray,
  isBoolean,
  isEmpty,
  isNaN,
  isNull,
  isNumber,
  isObject,
  isString,
  isUndefined,
} from 'lodash-es';

export const isRepCodeSelectionComplete = (value: unknown) => {
  if (!isObject(value)) return false;
  const code = (value as { code?: string }).code;
  return typeof code === 'string' && code.trim() !== '';
};

export type FormError = {
  active: boolean;
  message?: string;
};
export type FieldOption = {
  required: boolean;
  errorMessage?: string;
  requiredIf?: (data: any) => boolean;
};
export type FormErrors = { [fieldId: string]: FormError } | null;
export type FieldOptions = { [fieldId: string]: FieldOption };

export const createError = (message = t('shared:required')): FormError => ({
  active: true,
  message,
});

export const validateFields = (
  fieldOptions: FieldOptions,
  data: { [fieldId: string]: any },
  candidateFields?: string[],
): FormErrors => {
  const errors: FormErrors = {};
  const fields = invalidFields(fieldOptions, data);
  fields
    .filter((field) =>
      candidateFields ? candidateFields.includes(field) : true,
    )
    .forEach((field) => {
      errors[field] = createError(fieldOptions[field].errorMessage);
    });
  return isEmpty(errors) ? null : errors;
};

const getNestedValue = (obj: any, path: string) =>
  path.split('.').reduce((acc, key) => acc && acc[key], obj);

export const invalidFields = (
  options: { [fieldID: string]: { [optionName: string]: any } },
  data: { [fieldID: string]: any },
) => {
  const missingFields: string[] = [];

  Object.entries(options).forEach(([fieldId, fieldOptions]) => {
    if (!fieldOptions) return;
    let value = data[fieldId];
    const isEnabledCustomField = Boolean(
      options[fieldId]?.customField && options[fieldId]?.enabled,
    );
    let isBooleanCustomField = false;

    // Handle trusted contact fields
    if (fieldId.includes('trustedContact')) {
      switch (fieldId) {
        case 'trustedContactName':
          value = getNestedValue(data, 'trustedContactPerson.name');
          break;

        case 'trustedContactEmail':
          value = getNestedValue(data, 'trustedContactPerson.email');
          break;

        case 'trustedContactPhone':
          value = getNestedValue(data, 'trustedContactPerson.phone');
          break;

        case 'trustedContactAddress':
          value = getNestedValue(data, 'trustedContactPerson.physicalAddress');
          break;

        case 'trustedContactRelation':
          value = getNestedValue(data, 'trustedContactPerson.relation');
          break;

        default:
          break;
      }
    }

    // Check for custom fields, rules and data
    if (isEnabledCustomField) {
      const customFieldData = data?.customFields?.find(
        (option: any) => option.key === fieldId,
      );
      const customFieldConfig = options[fieldId]?.customField;

      // Early returns for cases where we don't need validation
      if (!customFieldConfig) return;
      if (!evaluateTriggerRules(customFieldConfig, data)) return;
      if (customFieldConfig.format === CustomFieldFormats.Custom) return;

      isBooleanCustomField =
        customFieldConfig.type === CustomFieldTypes.Boolean;

      // the custom field data is required by rules
      value = customFieldData?.value;
    }

    const isEnabled = fieldOptions.enabled ?? true;
    const isRequired = isBoolean(fieldOptions.required)
      ? fieldOptions.required
      : fieldOptions.required === FieldRequiredType.ERROR;
    const isRequiredConditionally =
      fieldOptions.requiredIf === undefined || fieldOptions.requiredIf(data);

    const repCodeIncomplete =
      fieldId === 'repCode' && !isRepCodeSelectionComplete(value);

    const isIncomplete =
      isUndefined(value) ||
      // Untouched booleans are persisted as `false`, so `false` cannot be
      // distinguished from unanswered and must not satisfy a required checkbox.
      // Scoped to custom fields: built-in toggles accept `false` as an answer.
      (isBooleanCustomField && value !== true) ||
      (isNumber(value) && isNaN(value)) ||
      (isString(value) && value.trim() === '') ||
      isNull(value) ||
      value === '' ||
      ((isObject(value) || isArray(value)) && isEmpty(value)) ||
      repCodeIncomplete ||
      (fieldId.includes('physicalAddress') && !value.streetName) ||
      (fieldId.includes('mailingAddress') && !value.streetName) ||
      (fieldId.includes('alternateAddress') && !value.line1) ||
      (fieldId === 'sin' && value.length !== 9) ||
      (fieldId === 'foreignTaxInformation' &&
        value.filter(
          (v: ForeignTaxInformation) =>
            v.foreignTaxNumber === '' || v.foreignTaxCountry === '',
        ).length > 0);
    if (isEnabled && isRequired && isRequiredConditionally && isIncomplete)
      missingFields.push(fieldId);
  });

  return missingFields;
};

export function base64ToFile(base64Data: string, fileName: string): File {
  const dataParts = base64Data.split(';base64,');
  const contentType = dataParts[0].replace('data:', '');
  const decodedData = atob(dataParts[1]);
  const buffer = new ArrayBuffer(decodedData.length);
  const arrayView = new Uint8Array(buffer);
  for (let i = 0; i < decodedData.length; i++) {
    arrayView[i] = decodedData.charCodeAt(i);
  }
  const blob = new Blob([buffer], {
    type: contentType || 'application/octet-stream',
  });
  return new File([blob], fileName, {
    type: contentType || 'application/octet-stream',
  });
}

export type WorkflowCustomFieldOption = {
  enabled?: boolean;
  customField?: { key?: string | null } | null;
};

export const getEnabledCustomFields = (
  options?: Record<string, WorkflowCustomFieldOption>,
): string[] => {
  if (!options) return [];

  return Object.values(options)
    .filter(
      (
        option,
      ): option is WorkflowCustomFieldOption & {
        enabled: true;
        customField: { key: string };
      } => Boolean(option?.enabled && option.customField?.key),
    )
    .map((option) => option.customField.key);
};

export const convertToDotNotation = (graphqlString: string): string[] => {
  const keys: string[] = [];
  const lines: string[] = graphqlString.split('\n');
  const stack: string[] = [];

  // Function to process a single line and determine the full key
  const processLine = (line: string): void => {
    const trimmed = line.trim();

    // Ignore empty lines or comments
    if (!trimmed || trimmed.startsWith('#')) return;

    // Match fields with or without a nested block
    const match = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*(\{)?$/);

    if (match) {
      const key = match[1];
      const hasNested = !!match[2];

      // Build the full key based on the stack
      const fullKey = stack.length ? `${stack.join('.')}.${key}` : key;

      // Add the key to the result
      keys.push(fullKey);

      // If there's a nested block, push the current key to the stack
      if (hasNested) {
        stack.push(key);
      }
    } else if (trimmed === '}') {
      // Close the current block by popping from the stack
      stack.pop();
    }
  };

  // Process all lines in the GraphQL string
  for (const line of lines) {
    processLine(line);
  }

  return keys;
};

const formatNumberWithDashes = (number: string, format: string): string => {
  let numberIdx = 0;
  let dashedNumber = '';
  for (let i = 0; i < format.length; ++i) {
    if (format[i] === 'x' && number[numberIdx]) {
      dashedNumber += number[numberIdx];
      ++numberIdx;
    } else {
      dashedNumber += format[i];
    }
  }

  return dashedNumber;
};

export const showAccountNumber = (
  accountNumber: string,
  isMasked = false,
  dashFormat?: string,
) => {
  let number = accountNumber;
  if (isMasked) {
    number = truncateNumber(number);
  }
  if (dashFormat) {
    const regexp = new RegExp(
      dashFormat.replaceAll('-', '').replaceAll('x', '.'),
    );
    if (regexp.test(accountNumber)) {
      number = formatNumberWithDashes(number, dashFormat);
    }
  }
  return number;
};

export const generateRandomNumber = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Builds the custom options array for rendering, enriching stale workflow config
 * data with fresh translations from the org service's custom field definitions.
 *
 * The workflow `options` blob contains a snapshot of each custom field's
 * `selectOptions` / `translatedName` / `translatedDescription` taken at config
 * time. These can become stale when translations are added later.
 * `derivedCustomFields` contains the live data from `FETCH_CUSTOM_FIELDS`.
 */
export const buildEnrichedCustomOptions = (
  options: Record<string, any>,
  activeCustomFields: string[] | undefined,
  derivedCustomFields: CustomField[] | undefined,
): CustomOption[] => {
  const customOptions: CustomOption[] = [];

  Object.keys(options).forEach((key: string) => {
    if (
      options[key]?.customField &&
      options[key].enabled &&
      activeCustomFields?.includes(key)
    ) {
      const freshField = derivedCustomFields?.find((cf) => cf.key === key);

      if (freshField) {
        customOptions.push({
          ...options[key],
          customField: {
            ...options[key].customField,
            selectOptions:
              freshField.selectOptions ??
              options[key].customField.selectOptions,
            translatedName:
              freshField.translatedName ??
              options[key].customField.translatedName,
            translatedDescription:
              freshField.translatedDescription ??
              options[key].customField.translatedDescription,
          },
        });
      } else {
        customOptions.push(options[key]);
      }
    }
  });

  return customOptions;
};
